// client/src/coms/views/watch_together_parts/watchSync.js
// Pure synchronisation rules for Watch Together (unit tested, no React / LiveKit imports).
//
// - Ordering: Lamport revisions (not wall clocks) so participants with skewed clocks still converge.
// - Playback: a shared timeline { currentTime at updatedAt, playing }; players are corrected only when
//   drift is meaningful, never on every update.
// - Control: optional host-only mode; the host is re-elected deterministically when it leaves.
// - Local intent: pauses/plays/seeks made with the embedded player's own controls are detected and
//   shared (or undone when the viewer may not control playback).

// ---- ordering ----

export function nextRevision(current, incoming = 0) {
  return Math.max(Number(current) || 0, Number(incoming) || 0) + 1;
}

// Is (rev, from) newer than the currently applied (rev, from)? Ties break on identity so every
// participant picks the same winner.
export function isNewerRevision(incoming, current) {
  const ir = Number(incoming && incoming.rev) || 0;
  const cr = Number(current && current.rev) || 0;
  if (ir !== cr) return ir > cr;
  return String((incoming && incoming.from) || '') > String((current && current.from) || '');
}

// ---- playback timeline ----

export function expectedPosition(playback, now = Date.now()) {
  if (!playback) return 0;
  const base = Math.max(0, Number(playback.currentTime) || 0);
  if (!playback.playing) return base;
  const at = Number(playback.updatedAt) || now;
  return base + Math.max(0, (now - at) / 1000);
}

export const DRIFT_SEEK_PLAYING_S = 1.5; // correct when this far out while playing
export const DRIFT_SEEK_PAUSED_S = 0.5; // paused players should show the same frame
export const DRIFT_SEEK_BUFFERING_S = 5; // a buffering player catches up by itself unless far behind

// -> { seekTo: number|null, play: boolean, pause: boolean }
export function computeCorrection({ localTime, localPlaying, localBuffering = false, playback, now = Date.now() }) {
  const out = { seekTo: null, play: false, pause: false };
  if (!playback) return out;
  const target = expectedPosition(playback, now);
  const drift = Math.abs((Number(localTime) || 0) - target);
  const limit = !playback.playing ? DRIFT_SEEK_PAUSED_S : localBuffering ? DRIFT_SEEK_BUFFERING_S : DRIFT_SEEK_PLAYING_S;
  if (drift > limit) out.seekTo = Math.max(0, target);
  if (playback.playing && !localPlaying && !localBuffering) out.play = true;
  if (!playback.playing && localPlaying) out.pause = true;
  return out;
}

// ---- local intent detection ----
// prev / cur: { time, at, playing, buffering } samples from the embedded player. prev is the last
// sample that was not buffering (a seek almost always buffers, so comparing neighbouring samples
// missed it). stalled: the player buffered in between, so it advanced anywhere from 0 to elapsed.
// ignoreUntil: ms timestamp; changes before it were caused by our own commands.
export const SEEK_JUMP_S = 2.5;

export function detectLocalIntent({ prev, cur, playback, ignoreUntil = 0, stalled = false }) {
  if (!prev || !cur || !playback) return null;
  if (cur.at <= ignoreUntil) return null;
  if (cur.buffering || prev.buffering) return null;

  const elapsed = Math.max(0, (cur.at - prev.at) / 1000);
  const advanced = (Number(cur.time) || 0) - (Number(prev.time) || 0);
  const maxAdvance = prev.playing ? elapsed : 0;
  const minAdvance = prev.playing && !stalled ? elapsed : 0;
  if (advanced > maxAdvance + SEEK_JUMP_S || advanced < minAdvance - SEEK_JUMP_S) {
    // A seek keeps the shared play state: the player reports "paused" for a moment while seeking.
    return { kind: 'seek', time: Math.max(0, Number(cur.time) || 0), playing: !!playback.playing };
  }
  if (playback.playing && prev.playing && !cur.playing) return { kind: 'pause', time: Math.max(0, Number(cur.time) || 0) };
  if (!playback.playing && !prev.playing && cur.playing) return { kind: 'play', time: Math.max(0, Number(cur.time) || 0) };
  return null;
}

// ---- control ----

export function canControl({ mode, hostIdentity, identity }) {
  if (String(mode || 'everyone') !== 'host') return true;
  const host = String(hostIdentity || '');
  return !host || host === String(identity || '');
}

// Keeps the current host while present; otherwise the lowest identity among present participants.
export function electHost(currentHost, presentIdentities) {
  const present = Array.from(new Set((presentIdentities || []).map((x) => String(x || '')).filter(Boolean))).sort();
  const cur = String(currentHost || '');
  if (cur && present.includes(cur)) return cur;
  return present[0] || '';
}

// ---- errors ----

export function describeYouTubeError(code) {
  switch (Number(code)) {
    case 2:
      return 'That YouTube link is not valid.';
    case 5:
      return 'This video cannot be played in the embedded player.';
    case 100:
      return 'This video is unavailable (removed or private).';
    case 101:
    case 150:
    case 153:
      return 'The owner of this video does not allow it to be played on other sites. Skip it or open it on YouTube.';
    default:
      return 'The video could not be played.';
  }
}
